import { gql } from '@apollo/client';

/**
 * Pre-computed PandaDoc analytics — KPIs and chart series for the dashboard.
 * The backend syncs documents hourly and aggregates everything; the frontend
 * just renders the response and re-queries when a filter changes.
 */
export const PandaDocAnalytics = gql`
  query PandaDocAnalytics(
    $granularity: String
    $course: String
    $docType: String
    $dateFrom: Date
    $dateTo: Date
  ) {
    pandadocAnalytics(
      granularity: $granularity
      course: $course
      docType: $docType
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      granularity
      course
      window {
        docsLoaded
        totalInDb
        signedCount
        minDate
        maxDate
      }
      volumeKpi {
        total
        students
        trainers
        dofin
        last30
      }
      ttsKpi {
        medianHours
        meanHours
        pct24h
        pct3d
        sample
      }
      conversionKpi {
        sent
        signed
        active
        lost
        signedPct
        activePct
        lostPct
        ratePct
      }
      volumeSeries {
        students {
          labels
          values
        }
        trainers {
          labels
          values
        }
        dofin {
          labels
          values
        }
      }
      ttsHistogram {
        labels
        values
      }
      ttsByCourse {
        course
        avgDays
        sample
      }
      ttsMedianSeries {
        students {
          labels
          values
        }
        trainers {
          labels
          values
        }
        dofin {
          labels
          values
        }
      }
      conversionByCourse {
        course
        signed
        active
        lost
      }
      conversionSeries {
        students {
          labels
          signed
          active
          lost
        }
        dofin {
          labels
          signed
          active
          lost
        }
      }
    }
  }
`;

/** Relay connection backing the documents table at the bottom of the page. */
export const PandaDocDocuments = gql`
  query PandaDocDocuments(
    $course: String
    $docType: String
    $outcome: String
    $search: String
    $dateFrom: Date
    $dateTo: Date
    $first: Int
    $after: String
  ) {
    pandadocDocuments(
      course: $course
      docType: $docType
      outcome: $outcome
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          pk
          pandadocId
          name
          status
          docType
          course
          outcome
          ttsHours
          dateCreated
          dateModified
          dateSent
          dateCompleted
        }
      }
    }
  }
`;

/**
 * Folders of signed student contracts in PandaDoc, ready for provisioning.
 * Folder name == expected group name; the backend syncs PandaDoc hourly.
 */
export const PandaDocProvisioningFolders = gql`
  query PandaDocProvisioningFolders($course: String) {
    pandadocProvisioningFolders(course: $course) {
      folderUuid
      folderName
      detectedCourseId
      detectedCourseName
      existingGroupId
      existingGroupName
      studentDocs {
        pandadocId
        pk
        name
        status
        outcome
        firstName
        lastName
        email
        phone
        contractUrl
        phoneMissing
        userExists
        studentExists
        existingUserId
        alreadyProvisioned
      }
    }
  }
`;

/** Sync health — lets the UI warn when conversion data is still incomplete. */
export const PandaDocSyncStatus = gql`
  query PandaDocSyncStatus {
    pandadocSyncStatus {
      documentsCount
      detailsPending
      lastListSyncAt
      lastDetailsSyncAt
      lastError
    }
  }
`;
